import React, { useState } from "react";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";

export type DateRangeType = {
  startDate: any;
  endDate: any;
};

type DateRangeSelectorProps = {
  initialRange?: DateRangeType;
  onChange: (range: DateRangeType) => void;
  className?: string;
  /** When true, disables every date after today (useful for "Created" filters). */
  disableFuture?: boolean;
  placeholder?: string;
  id?: string;
  name?: string;
};

const isValidDate = (d: Date | null): d is Date =>
  d instanceof Date && !Number.isNaN(d.getTime());

const normalizeRange = (range: [Date | null, Date | null]): DateRangeType => {
  const [start, end] = range;

  const normalizedStart = isValidDate(start)
    ? new Date(start.setHours(0, 0, 0, 0)) // Set time to 12:00 AM
    : null;

  const normalizedEnd = isValidDate(end)
    ? new Date(end.setHours(23, 59, 59, 999)) // Set time to 11:59 PM
    : null;

  return { startDate: normalizedStart, endDate: normalizedEnd };
};

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  initialRange = { startDate: null, endDate: null },
  onChange,
  className = "",
  disableFuture = false,
  placeholder = "startDate - endDate",
  id,
  name,
}) => {
  const [range, setRange] = useState<[Date | null, Date | null]>([
    initialRange.startDate,
    initialRange.endDate,
  ]);

  const handleDateChange = (value: [Date | null, Date | null] | null) => {
    if (value) {
      const normalized = normalizeRange(value);
      // If either side ends up invalid (e.g. user is mid-typing), don't emit
      // a partial range upstream — wait until both ends are valid dates.
      if (
        (value[0] && !normalized.startDate) ||
        (value[1] && !normalized.endDate)
      ) {
        return;
      }
      setRange([normalized.startDate, normalized.endDate]);
      onChange(normalized);
    } else {
      setRange([null, null]);
      onChange({ startDate: null, endDate: null });
    }
  };

  return (
    <div className={className}>
      <DateRangePicker
        id={id}
        name={name}
        value={range[0] && range[1] ? (range as [Date, Date]) : null}
        onChange={handleDateChange}
        placeholder={placeholder}
        className="z-10"
        block
        cleanable
        format="yyyy-MM-dd"
        shouldDisableDate={
          disableFuture ? DateRangePicker.afterToday() : undefined
        }
      />
    </div>
  );
};

export default DateRangeSelector;
